import React, { useState, useRef, useEffect, useContext } from "react";
import { FaLock } from "react-icons/fa";
import { X } from "lucide-react";
import { sendOtp, verifyOtp } from "../../services/SignIn/authApi";
import { UserContext } from "../../context/UserContext";
import { requestPermission } from "../../firebase/messaging";
import { toast } from "react-toastify";
import { usePopupLock } from "../../context/PopupLockContext";

export default function LoginPopup({ isOpen, onClose, disabled }) {
  const { loginUser } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  const [step, setStep] = useState("choice");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState("");
  
  // Stronger guards
  const [isClosing, setIsClosing] = useState(false);
  const hasOpenedRef = useRef(false);
  const closeTimeoutRef = useRef(null);
  const openTimeRef = useRef(null); // ✅ NEW - ADD THIS LINE

  const firstOtpRef = useRef(null);
  const popupRef = useRef(null);

  // Popup lock management
  useEffect(() => {
    if (isOpen && !disabled && !isClosing) {
      openLock("login");
      console.log("🔒 Login popup locked");
    } else {
      closeLock("login");
      console.log("🔓 Login popup unlocked");
    }
    
    return () => {
      closeLock("login");
    };
  }, [isOpen, disabled, isClosing, openLock, closeLock]);

  // ✅ 5 MINUTE AUTO-CLOSE TIMER
  useEffect(() => {
    if (isOpen && !disabled && !isClosing) {
      openTimeRef.current = Date.now();
      console.log("🔓 Popup opened at:", new Date().toLocaleTimeString());
      
      const autoCloseTimer = setTimeout(() => {
        console.log("⏱️ 5 minutes completed. Auto-closing popup...");
        handleSafeClose();
      }, 300000); // 5 * 60 * 1000
      
      return () => clearTimeout(autoCloseTimer);
    }
  }, [isOpen, disabled, isClosing]);

  // RESET WHEN OPEN - with stronger guard
  useEffect(() => {
    if (isOpen && !disabled && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setStep("choice");
      setIdentifier("");
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setTimeLeft(0);
      setIsClosing(false);
      
      setTimeout(() => {
        hasOpenedRef.current = false;
      }, 300);
    }
    
    if (!isOpen) {
      hasOpenedRef.current = false;
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    }
  }, [isOpen, disabled]);

  // AUTO CLOSE IF DISABLED
  useEffect(() => {
    if (disabled && isOpen && !isClosing) {
      handleSafeClose();
    }
  }, [disabled, isOpen, isClosing]);

  // ESC CLOSE
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen && !isClosing && !loading) {
        handleSafeClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, isClosing, loading]);

  // CLICK OUTSIDE CLOSE
  const handleOutsideClick = (e) => {
    if (disabled || isClosing || loading) return;

    if (popupRef.current && !popupRef.current.contains(e.target)) {
      handleSafeClose();
    }
  };

  // AUTO FOCUS OTP
  useEffect(() => {
    if (step === "otp" && isOpen && !isClosing) {
      setTimeout(() => {
        firstOtpRef.current?.focus();
      }, 100);
    }
  }, [step, isOpen, isClosing]);

  // TIMER
  useEffect(() => {
    if (!timeLeft) return;
    const t = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // ✅ UPDATED - SAFE CLOSE FUNCTION with duration tracking
  const handleSafeClose = () => {
    if (isClosing || !isOpen) return;
    
    // Duration calculate karein
    if (openTimeRef.current) {
      const duration = (Date.now() - openTimeRef.current) / 1000;
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      console.log(`⏱️ Popup was open for: ${minutes}m ${seconds}s`);
    }
    
    setIsClosing(true);
    
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      setTimeout(() => {
        setIsClosing(false);
        hasOpenedRef.current = false;
      }, 100);
    }, 50);
  };

  // BLOCK RENDER
  if (!isOpen || disabled || isClosing) return null;

  // SEND OTP
  const handleSendOtp = async () => {
    if (!identifier) {
      setError("Enter mobile or email");
      return;
    }

    setLoading(true);
    setError("");
    const res = await sendOtp(identifier);
    setLoading(false);

    if (res?.success) {
      setStep("otp");
      setTimeLeft(30);
      setOtp(["", "", "", "", "", ""]);
      toast.success("OTP Sent");
    } else {
      setError(res?.error || "Failed to send OTP");
    }
  };

  // VERIFY OTP
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setError("Enter complete OTP");
      return;
    }

    setLoading(true);
    const res = await verifyOtp(identifier, enteredOtp);
    setLoading(false);

    if (res?.success) {
      const fcmToken = await requestPermission();

      loginUser({
        userId: res.user?.id,
        identifier,
        token: res.token,
        fcmToken,
      });

      toast.success("Login Successful ✅");
      handleSafeClose();
    } else {
      setError(res?.error || "Invalid OTP");
    }
  };

  // OTP INPUT
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
    if (!value && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  return (
    <div
      onClick={handleOutsideClick}
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 transition-all duration-300"
    >
      <div
        ref={popupRef}
        className="relative w-full max-w-[440px] bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 animate-scaleIn"
      >
        {/* TOP BRANDED HEADER */}
        <div className="relative bg-gradient-to-r from-red-600 via-rose-600 to-red-500 p-6 text-white text-left">
          {/* Subtle pattern background */}
          <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/10 rounded-full blur-xl" />
          
          <button
            onClick={handleSafeClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all duration-200 cursor-pointer"
          >
            <X size={16} />
          </button>

          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center font-black text-sm">
              🏥
            </div>
            <span className="text-[11px] font-black uppercase tracking-widest text-red-100 bg-red-700/40 px-2.5 py-0.5 rounded-full">
              Medrpha Labs
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
            {step === "otp" ? "Verify OTP" : "Welcome to Medrpha"}
          </h2>
          <p className="text-xs text-red-100 mt-1 font-medium">
            {step === "otp" 
              ? `Enter 6-digit code sent to ${identifier}` 
              : "Login or Sign Up to book tests & access lab reports"}
          </p>
        </div>

        {/* BODY */}
        <div className="p-6 sm:p-7">
          {step === "otp" ? (
            /* OTP STEP */
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-slate-600 font-medium">
                <span>Verification Code</span>
                <button
                  onClick={() => {
                    setStep("form");
                    setOtp(["", "", "", "", "", ""]);
                    setError("");
                  }}
                  className="text-red-600 font-bold hover:underline"
                >
                  Change Number
                </button>
              </div>

              {/* 6 OTP BOXES */}
              <div className="grid grid-cols-6 gap-2">
                {otp.map((d, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    ref={i === 0 ? firstOtpRef : null}
                    value={d}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                    maxLength={1}
                    className="w-full h-12 text-center text-lg font-black bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 focus:outline-none transition-all"
                  />
                ))}
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-2.5 rounded-xl text-xs font-semibold flex items-center gap-1.5">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <div className="flex items-center justify-between text-xs pt-1">
                {timeLeft > 0 ? (
                  <span className="text-slate-400 font-medium">Resend OTP in <b>{timeLeft}s</b></span>
                ) : (
                  <button
                    onClick={handleSendOtp}
                    className="text-red-600 font-extrabold hover:underline"
                  >
                    Resend OTP
                  </button>
                )}
                <span className="text-slate-400 text-[11px]">Free SMS & WhatsApp</span>
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black py-3 rounded-2xl transition-all shadow-md shadow-red-500/25 active:scale-98 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Verifying Code...</span>
                  </div>
                ) : (
                  <span>Verify & Proceed</span>
                )}
              </button>
            </div>
          ) : (
            /* PHONE/EMAIL INPUT STEP (Clean Direct View) */
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                  Mobile Number or Email
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter 10-digit mobile number"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSendOtp();
                    }}
                    autoFocus
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-900 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 focus:outline-none transition-all placeholder:text-slate-400"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-2.5 rounded-xl text-xs font-semibold flex items-center gap-1.5">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleSendOtp}
                disabled={loading || !identifier.trim()}
                className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black py-3.5 rounded-2xl transition-all shadow-md shadow-red-500/25 active:scale-98 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sending OTP...</span>
                  </div>
                ) : (
                  <span>Continue with OTP</span>
                )}
              </button>

              <div className="pt-2 border-t border-slate-100 text-center">
                <p className="text-[11px] text-slate-400">
                  By continuing, you agree to Medrpha's{" "}
                  <a href="/terms-conditions" className="text-slate-600 underline font-medium">Terms</a> &{" "}
                  <a href="/privacy-policy" className="text-slate-600 underline font-medium">Privacy Policy</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}